/**
 * Spanish copy.
 *
 * Written in Spanish, not translated from the English. The brand documents are
 * explicit about this: "Los dos compraron leche. Nadie compró pan." is its own
 * sentence, not a rendering of the English hero line, and the vocabulary is
 * what people actually say — el súper, el mandado, la despensa.
 *
 * Neutral Latin American Spanish, using "tú" throughout — the brand voice is a
 * flatmate, not a bank. Mexico is the home market.
 *
 * The brand name is never translated: it is "Two Milks" here too.
 *
 * Typed as `Dictionary`, so if a key is added to en.ts and forgotten here, the
 * build fails instead of shipping an English string into the Spanish site.
 *
 * Note: for the legal notices, the English version is the binding one. That is
 * stated on the page itself and in docs/legal.html.
 */
import type { Dictionary } from './types';

const es: Dictionary = {
  meta: {
    title: 'Two Milks — una sola lista del súper para toda la casa',
    description:
      'Una lista para toda la casa, que se tacha en el momento en que alguien agarra algo. Agrégalo desde el camión. Toma la foto del exacto que sí sirve.',
  },

  nav: {
    how: 'Cómo funciona',
    features: 'Qué hace',
    faq: 'Preguntas',
    pricing: 'Precios',
    cta: 'Descargar',
    skipToContent: 'Saltar al contenido',
    menu: 'Menú',
    closeMenu: 'Cerrar menú',
    languageLabel: 'Idioma',
  },

  /*
   * Los dos botones de tienda, que usan el hero y el panel de cierre. Se
   * comparten en vez de duplicarse para que no se desincronicen.
   */
  store: {
    ios: 'Descargar para iPhone',
    android: 'Descargar para Android',
  },

  hero: {
    // Dos líneas, a propósito. El chiste está en el salto de línea.
    headlineA: 'Los dos compraron leche.',
    headlineB: 'Nadie compró pan.',
    body: 'Una lista para toda la casa, que se tacha en el momento en que alguien agarra algo. Agrégalo desde el camión. Toma la foto del exacto que sí sirve.',
    reassurance:
      'Gratis para una casa de tres. No nos conectamos con ninguna tienda, y nunca vendemos lo que compras.',
  },

  mockup: {
    houseLabel: 'Casa',
    houseName: 'Flat 4B',
    houseMeta: '6 faltan · 1 comprado',
    shop: 'Ir a comprar',
    add: 'Agregar algo',
    produce: 'Frutas y verduras',
    dairy: 'Lácteos y huevo',
    items: {
      tomatoes: 'Jitomate saladet',
      lemons: 'Limones',
      oatMilk: 'Leche de avena barista',
      coriander: 'Cilantro',
      butter: 'Mantequilla con sal',
      eggs: 'Huevo de libre pastoreo',
    },
    by: {
      tomatoes: 'Mari',
      lemons: 'Sam · foto',
      oatMilk: 'Mari · foto',
      coriander: 'Tom',
      butter: 'Tom',
      eggs: 'Mari',
    },
    tabs: {
      list: 'LISTA',
      feed: 'ACTIVIDAD',
      house: 'CASA',
      settings: 'AJUSTES',
    },
  },

  problems: {
    items: [
      {
        title: 'Dos leches',
        body: 'Los dos compraron una. Las cosas se tachan en el momento en que alguien las agarra, así el segundo viaje no repite el primero.',
      },
      {
        title: 'El equivocado',
        body: '"No el de semillas." Toma la foto del producto exacto y la nota viaja con la cosa hasta el pasillo.',
      },
      {
        title: 'Quién pagó',
        body: 'Cada viaje guarda el total y cómo se divide, así las cuentas de fin de mes ya están hechas.',
      },
    ],
  },

  how: {
    heading: 'Tres toques para empezar. Uno para todo lo demás.',
    steps: [
      {
        number: '01',
        title: 'Crea la casa',
        body: 'Ponle nombre al lugar y te da un código como',
        code: '4B-PLUM',
      },
      {
        number: '02',
        title: 'Manda el código',
        body: 'Tu pareja, tus roomies, tu mamá. Lo escriben una vez y ya están — sin cuentas que administrar.',
      },
    ],
    feature: {
      number: '03',
      title: 'Agrégalo en cuanto lo notes',
      body: 'Se acabó la última cápsula de café — agrégala ahí parado. Ese es todo el hábito, y es el único que funciona.',
    },
  },

  features: {
    heading: 'Hecho para cómo compra una casa de verdad.',
    photos: {
      title: 'Fotos para lo específico',
      body: 'Tómale foto a la etiqueta o al precio del anaquel. Quien va de compras la ve en pantalla completa en el pasillo, en vez de adivinar entre once leches de avena.',
      note: '"Solo la barista hace espuma — la normal se corta en el café."',
      shelfTag: 'El anaquel',
      label: 'La etiqueta',
    },
    categories: {
      title: 'Tus categorías, tus pasillos',
      body: 'Cámbiales el nombre y arrástralas al orden de tu súper de verdad. Toda la casa ve el mismo orden.',
      rows: [
        { label: 'Frutas y verduras', count: '6' },
        { label: 'Panadería', count: '2' },
        { label: 'Lácteos y huevo', count: '4' },
      ],
    },
    shopping: {
      overline: 'Modo compras',
      title: 'Una pantalla que se lee a un brazo de distancia.',
      body: 'Empieza un viaje y la lista se pone grande, oscura y en orden de pasillo. Botones grandes, nada más en pantalla, y en la casa ven cómo se va tachando mientras caminas.',
      stats: [
        { value: '18px', label: 'texto de fila, con una mano' },
        { value: 'En vivo', label: 'se sincroniza mientras caminas' },
      ],
      progressLabel: 'de 14 faltan',
      shopper: 'Sam está comprando',
      items: ['Jitomate saladet', 'Leche de avena barista', 'Espinacas'],
    },
  },

  pricing: {
    title: 'Gratis. Ese es el plan.',
    body: 'Todo lo que necesitas para dejar de comprar dos veces lo mismo, para toda la casa que lo comparte \u2014 y nada de lo que la casa apunta se borra.',
    free: {
      name: 'La casa',
      price: 'Gratis',
      per: 'Para siempre, para hasta tres personas.',
      items: [
        'Una lista compartida, que se tacha en vivo',
        'Fotos y notas en cualquier cosa',
        'Modo compras, sin conexión',
        'Totales del viaje y cómo se divide',
        '30 días de historial',
      ],
      cta: 'Crear una casa',
    },
    kept: {
      name: 'La forma de una casa',
      note: 'Los límites, dichos claro — y lo que nunca cambia.',
      items: [
        'Tres personas por casa',
        'Dos casas por persona',
        'Los últimos 30 días de mandados a la vista',
        'Todo lo más viejo sigue en el registro',
        'Nada se borra, nunca',
      ],
    },
    whyTitle: 'Por qué hay límites.',
    whyBody:
      'Two Milks está decidiendo qué será, y los límites son su manera de escuchar. Son reales — tres personas, 30 días a la vista — pero nada más viejo se borra, así que si algún día los límites se abren, todo lo de antes sigue ahí.',
  },

  faq: {
    heading: 'Lo que preguntan antes de invitar a la casa.',
    items: [
      {
        q: '¿Todos necesitan cuenta?',
        a: 'Una persona crea la casa; los demás entran con el código. La cuenta es solo un correo y una contraseña.',
      },
      {
        q: '¿Y si no hay señal en la tienda?',
        a: 'Todo funciona sin conexión y se sincroniza en cuanto regresas — nada de lo que tachas en el sótano del súper se pierde.',
      },
      {
        q: '¿Cuánto cuesta?',
        a: 'Es gratis. Una casa es para tres personas, una persona puede estar en hasta dos casas, y la app muestra los últimos 30 días de mandados; todo lo más viejo sigue en el registro y nunca se borra.',
      },
      {
        q: '¿Cuándo sale?',
        a: 'La estamos terminando. Únete a la lista de espera y te llega un solo correo el día que aparezca en el App Store y en Google Play — nada más.',
      },
    ],
  },

  finalCta: {
    heading: 'Saca la pregunta de la leche de tus mensajes.',
    body: 'Crea una casa en menos de un minuto. Invita a quien viva contigo.',
  },

  waitlist: {
    badge: 'Muy pronto',
    title: 'Two Milks todavía no sale.',
    body: 'Déjanos tu correo y te mandaremos un solo mensaje el día que llegue al App Store y a Google Play. Un correo. Nada más, nunca.',
    emailLabel: 'Tu correo electrónico',
    emailPlaceholder: 'tu@ejemplo.com',
    submit: 'Unirme a la lista',
    submitting: 'Enviando…',
    close: 'Cerrar',
    privacyNote: 'Solo usamos tu correo para avisarte que la app ya salió.',
    privacyLink: 'Cómo tratamos tus datos',
    successTitle: 'Ya estás en la lista.',
    successBody:
      'Revisa tu bandeja — va en camino una confirmación. Te escribimos el día del lanzamiento.',
    successDone: 'Cerrar',
    errors: {
      missing_email: 'Escribe tu correo electrónico.',
      invalid_email: 'Ese correo no parece válido.',
      email_too_long: 'Ese correo es demasiado largo.',
      unknown_product: 'Algo falló de nuestro lado. Inténtalo de nuevo.',
      rate_limited: 'Demasiados intentos. Espera unos minutos y vuelve a intentarlo.',
      spam_detected: 'Algo salió mal. Inténtalo de nuevo.',
      send_failed:
        'No pudimos guardarlo en este momento. Inténtalo otra vez o escríbenos a hola@buildpassing.com.',
      server_error:
        'Algo falló de nuestro lado. Inténtalo otra vez o escríbenos a hola@buildpassing.com.',
      network: 'No pudimos contactar al servidor. Revisa tu conexión e inténtalo de nuevo.',
    },
  },

  footer: {
    tagline: 'Una sola lista del súper para toda la casa.',
    builtBy: 'Hecho por',
    columns: {
      product: 'Producto',
      legal: 'Legal',
      company: 'Empresa',
    },
    links: {
      how: 'Cómo funciona',
      features: 'Qué hace',
      faq: 'Preguntas',
      pricing: 'Precios',
      waitlist: 'Lista de espera',
      privacy: 'Aviso de Privacidad',
      terms: 'Términos del Servicio',
      cookies: 'Política de Cookies',
      deleteAccount: 'Eliminar tu cuenta',
      support: 'Soporte',
      contact: 'Contacto',
      instagram: 'Instagram',
      buildpassing: 'BuildPassing',
    },
    rights: 'Todos los derechos reservados.',
  },

  notFound: {
    title: 'Esta página no existe.',
    body: 'Puede que el enlace esté desactualizado o que la hayamos movido. La portada es un buen lugar para empezar.',
    cta: 'Volver a la portada',
  },

  legal: {
    lastUpdated: 'Última actualización',
    effectiveDate: 'Fecha de entrada en vigor',
    contents: 'Contenido',
    questionsTitle: '¿Dudas sobre este documento?',
    questionsBody: 'Escríbenos y te responderá una persona.',
    bindingNotice:
      'Este documento también está disponible en inglés. Si ambas versiones difieren, prevalece la versión en inglés.',
  },
};

export default es;
